import { motion, AnimatePresence } from 'framer-motion'
import {
    X, LayoutDashboard, BookOpen, Users, FileText, Upload,
    MessageSquare, Building2, GraduationCap, Handshake, LogOut, Newspaper, Shield
} from 'lucide-react'
import { useAuth } from '@/context/auth-context'
import { useNavigate } from 'react-router-dom'

interface SubAdminMobileMenuProps {
    isOpen: boolean
    onClose: () => void
    activeTab: string
    onNavigate: (tab: string) => void
}

// Revenue & Payments removed
const coreItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'students', label: 'Students', icon: Users },
    { id: 'mentors', label: 'Assign Mentors', icon: Users },
    { id: 'counsellors', label: 'Manage Counsellors', icon: Users },
    // { id: 'revenue', label: 'Revenue & Payments', icon: IndianRupee },
    { id: 'tests', label: 'Create Tests', icon: FileText },
    { id: 'materials', label: 'Study Material', icon: Upload },
    { id: 'chats', label: 'Chats', icon: MessageSquare },
    { id: 'login-data', label: 'Login Data', icon: Shield },
]
const crmItems = [
    { id: 'abroad-colleges', label: 'Abroad Colleges', icon: GraduationCap },
    { id: 'abroad-leads', label: 'Abroad Leads', icon: Building2 },
    { id: 'coaching-tieups', label: 'Coaching Tie-ups', icon: Handshake },
    { id: 'partner-leads', label: 'Partner Leads', icon: Users },
    { id: 'news', label: 'News / Updates', icon: Newspaper },
    { id: 'blogs', label: 'Blog Posts', icon: BookOpen },
]

export function SubAdminMobileMenu({ isOpen, onClose, activeTab, onNavigate }: SubAdminMobileMenuProps) {
    const { user, logout } = useAuth()
    const navigate = useNavigate()

    const handleItemClick = (tab: string) => {
        onNavigate(tab)
        onClose()
    }

    const handleLogout = () => {
        logout()
        navigate('/login')
        onClose()
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 lg:hidden"
                    />
                    <motion.div
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '-100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white dark:bg-slate-900 shadow-2xl z-50 lg:hidden overflow-y-auto"
                    >
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center">
                                    <span className="text-white font-bold text-lg">FF</span>
                                </div>
                                <div>
                                    <h2 className="font-bold text-theme-primary">{user?.name || 'Sub Admin'}</h2>
                                    <p className="text-xs text-theme-secondary">Sub Admin Portal</p>
                                </div>
                            </div>
                            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                <X className="h-5 w-5 text-theme-secondary" />
                            </button>
                        </div>
                        <div className="p-3 space-y-1">
                            <p className="px-3 py-1.5 text-xs font-semibold text-theme-muted uppercase">Core</p>
                            {coreItems.map((item) => {
                                const Icon = item.icon
                                const isActive = activeTab === item.id
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleItemClick(item.id)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive ? 'bg-gradient-to-r from-primary-blue to-blue-600 text-white shadow-lg' : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-theme-primary'}`}
                                    >
                                        <Icon className={`h-5 w-5 ${isActive ? 'text-white' : ''}`} />
                                        <span className={`font-medium text-sm ${isActive ? 'text-white' : ''}`}>{item.label}</span>
                                    </button>
                                )
                            })}
                            <p className="px-3 py-1.5 text-xs font-semibold text-theme-muted uppercase mt-2">CRM</p>
                            {crmItems.map((item) => {
                                const Icon = item.icon
                                const isActive = activeTab === item.id
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleItemClick(item.id)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive ? 'bg-gradient-to-r from-primary-blue to-blue-600 text-white shadow-lg' : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-theme-primary'}`}
                                    >
                                        <Icon className={`h-5 w-5 ${isActive ? 'text-white' : ''}`} />
                                        <span className={`font-medium text-sm ${isActive ? 'text-white' : ''}`}>{item.label}</span>
                                    </button>
                                )
                            })}
                        </div>
                        <div className="p-4 border-t border-slate-200 dark:border-slate-800 mt-4 space-y-4">
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-red-600 transition-all"
                            >
                                <LogOut className="h-5 w-5" />
                                <span className="font-medium text-sm">Logout</span>
                            </button>
                            <p className="text-xs text-theme-secondary text-center">© 2025 Future Founder. Sub Admin.</p>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    )
}
